import React from 'react';
import { motion } from 'framer-motion';
import { LightbulbIcon, PowerIcon, ZapIcon } from 'lucide-react';
import { Sensor } from '../types';

interface ZoneDeviceStatusWidgetProps {
  sensors: Sensor[];
  sensorTelemetry?: Record<string, any>;
  selectedRoomId?: string;
  roomName?: string;
}

export function ZoneDeviceStatusWidget({
  sensors = [],
  sensorTelemetry = {},
  selectedRoomId = 'ALL',
  roomName = '전체공간',
}: ZoneDeviceStatusWidgetProps) {
  // 1. Filter Lighting sensors (SEL) -> light_state === 'on'
  const lightSensors = sensors.filter(
    (s) => s.type === 'SEL' || s.type === 'LIGHT' || s.type === 'LIGHTING' || s.name?.startsWith('SEL_') || s.label?.startsWith('SEL_')
  );
  const totalLights = lightSensors.length;
  const onLights = lightSensors.filter((s) => {
    const data = sensorTelemetry[s.id] || (s as any).telemetry;
    if (!data) return false;
    const lightState = data.light_state?.[0]?.value;
    return (
      lightState === 'on' ||
      lightState === 'true' ||
      lightState === true ||
      lightState === 1 ||
      lightState === '1'
    );
  }).length;

  // 2. Filter Smart Multi Outlet sensors (SEM) -> plug_on === 'on'
  const semSensors = sensors.filter(
    (s) => s.type === 'SEM' || s.name?.startsWith('SEM_') || s.label?.startsWith('SEM_')
  );
  const totalSems = semSensors.length;
  const onSems = semSensors.filter((s) => {
    const data = sensorTelemetry[s.id] || (s as any).telemetry;
    if (!data) return false;
    const plugOn = data.plug_on?.[0]?.value;
    return (
      plugOn === 'on' ||
      plugOn === 'true' ||
      plugOn === true ||
      plugOn === 1 ||
      plugOn === '1'
    );
  }).length;

  // 3. Filter Smart Outlet & Relay sensors (SEO, SER) -> plug_on === 'on'
  const seoSensors = sensors.filter(
    (s) => s.type === 'SEO' || s.type === 'SEM' || s.type === 'SER' || s.type === 'PLUG' || s.type === 'OUTLET' ||
           s.name?.startsWith('SEO_') || s.name?.startsWith('SEM_') || s.name?.startsWith('SER_') ||
           s.label?.startsWith('SEO_') || s.label?.startsWith('SEM_') || s.label?.startsWith('SER_')
  );
  const totalSeos = seoSensors.length;
  const onSeos = seoSensors.filter((s) => {
    const data = sensorTelemetry[s.id] || (s as any).telemetry;
    if (!data) return false;
    const plugOn = data.plug_on?.[0]?.value;
    return (
      plugOn === 'on' ||
      plugOn === 'true' ||
      plugOn === true ||
      plugOn === 1 ||
      plugOn === '1'
    );
  }).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="grid grid-cols-3 gap-2 w-full"
    >
      {/* 1. 전등(SEL) 위젯 */}
      <div className="py-3 px-2 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm flex flex-col items-center justify-center gap-1 min-h-[76px]">
        <div className="flex items-center gap-1.5">
          <LightbulbIcon className={`w-3.5 h-3.5 ${onLights > 0 ? 'text-amber-400' : 'text-slate-400'}`} />
          <span className="text-[0.8rem] font-bold tracking-tight text-[var(--mobile-text-muted)]">
            전등
          </span>
        </div>
        <div className="flex items-baseline gap-0.5">
          <span className={`text-xl font-mono font-bold ${onLights > 0 ? 'text-amber-400 glow-text-amber' : 'text-slate-400'}`}>
            {onLights}
          </span>
          <span className="text-xs font-mono text-[var(--mobile-text-muted)]">/{totalLights}</span>
          <span className="text-xs font-mono text-[var(--mobile-text-muted)]">개</span>
        </div>
      </div>

      {/* 2. 멀티탭(SEM) 위젯 */}
      <div className="py-3 px-2 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm flex flex-col items-center justify-center gap-1 min-h-[76px]">
        <div className="flex items-center gap-1.5">
          <PowerIcon className={`w-3.5 h-3.5 ${onSems > 0 ? 'text-blue-400' : 'text-slate-400'}`} />
          <span className="text-[0.8rem] font-bold tracking-tight text-[var(--mobile-text-muted)]">
            멀티탭
          </span>
        </div>
        <div className="flex items-baseline gap-0.5">
          <span className={`text-xl font-mono font-bold ${onSems > 0 ? 'text-blue-400 glow-text-blue' : 'text-slate-400'}`}>
            {onSems}
          </span>
          <span className="text-xs font-mono text-[var(--mobile-text-muted)]">/{totalSems}</span>
          <span className="text-xs font-mono text-[var(--mobile-text-muted)]">개</span>
        </div>
      </div>

      {/* 3. 콘센트(SEO/SER) 위젯 */}
      <div className="py-3 px-2 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm flex flex-col items-center justify-center gap-1 min-h-[76px]">
        <div className="flex items-center gap-1.5">
          <ZapIcon className={`w-3.5 h-3.5 ${onSeos > 0 ? 'text-emerald-400' : 'text-slate-400'}`} />
          <span className="text-[0.8rem] font-bold tracking-tight text-[var(--mobile-text-muted)]">
            콘센트
          </span>
        </div>
        <div className="flex items-baseline gap-0.5">
          <span className={`text-xl font-mono font-bold ${onSeos > 0 ? 'text-emerald-400 glow-text-emerald' : 'text-slate-400'}`}>
            {onSeos}
          </span>
          <span className="text-xs font-mono text-[var(--mobile-text-muted)]">/{totalSeos}</span>
          <span className="text-xs font-mono text-[var(--mobile-text-muted)]">개</span>
        </div>
      </div>
    </motion.div>
  );
}
